import {
  HttpStatusCode,
  retry,
  wait,
} from '@robovision/rvai-utils'
import axios, {
  AxiosError,
  type AxiosHeaderValue,
  type AxiosResponse,
} from 'axios'
import type { ApiErrorId } from '~domain/ApiErrorId'


export enum HttpHeaderName {
  Accept = 'Accept',
  ContentDisposition = 'Content-Disposition',
  ContentEncoding = 'Content-Encoding',
  ETag = 'ETag',
  IfNoneMatch = 'If-None-Match',
}

export function getResponseHeader (response: AxiosResponse, headerName: HttpHeaderName): AxiosHeaderValue {
  // Axios headers are always lowercase
  return response.headers[headerName.toLowerCase()]
}

export function acceptStatus (
  ...validStatus: number[]
): (status: number) => boolean {
  return (status: number) =>
    (status >= 200 && status < 300) || validStatus.includes(status)
}


interface RetryOptions {
  delay: number
  retries: number
}

export async function exponentialRetry<T> (
  request: () => Promise<T>,
  options: RetryOptions = {
    delay: 1000,
    retries: 3,
  },
): Promise<T> {
  let attempt = 0

  while (attempt < options.retries) {
    try {
      return await request()
    } catch (error: unknown) {
      if (!isNetworkError(error)) {
        throw error // rethrow the non network error
      }

      attempt++
      if (attempt >= options.retries) {
        throw new Error(`Max retries reached. ${error}`)
      }

      const delay = options.delay * Math.pow(5, attempt - 1) // 1, 5, 25, 125...

      await wait(delay)
    }
  }

  throw new Error('unreachable')
}

interface SpecificErrorCriteria {
  id: ApiErrorId
  statusCode: HttpStatusCode
}

export function isApiError (
  error: unknown,
  criteria: Partial<SpecificErrorCriteria>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): error is AxiosError<any, any> {
  if (!(error instanceof AxiosError)) return false

  const { id } = criteria
  if (id && error.response?.data.id !== id) return false

  const { statusCode } = criteria
  if (statusCode && error.response?.status !== statusCode) return false

  return true
}

export function isNetworkError (error: unknown): boolean {
  return axios.isAxiosError(error) && error?.code === AxiosError.ERR_NETWORK
}

export async function retryIfUnauthorized<T> (request: () => Promise<T>): Promise<T> {
  const result = await retry(
    async () => {
      try {
        const response = await request()
        return response
      } catch (e) {
        if (axios.isAxiosError(e) && e.response?.status === HttpStatusCode.UNAUTHORIZED) {
          // this is an unauthorized response just after the login
          // so this is most likely the issue described above
          throw e
        }
        // got another error, so we bail out
        console.error(e)
        throw new retry.CancelAsync()
      }
    },
    5,
  )

  return result
}

export async function throttlePromises<T> (
  ids: string[],
  loadFn: (id: string, signal: AbortSignal) => Promise<T>,
  signal: AbortSignal,
  batchSize: number = 5,
): Promise<void> {
  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize)
    await Promise.all(batch.map(id => loadFn(id, signal)))
  }
}
